import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr } from "@/lib/api";
import { Card, Field, Select, Btn, MpinField, ReceiptDialog, StatusBadge } from "@/components/shared";
import { useSubmit } from "@/pages/Services";
import { captureFinger, getGeo } from "@/lib/rdservice";

export const useCall = () => {
  const [busy, setBusy] = useState("");
  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); if (ok) toast.success(data?.message || ok); return data || true; } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };
  return { busy, call };
};

export const OtpDialog = ({ open, title, onSubmit, onClose, testid }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const go = async () => { setLoading(true); try { if (await onSubmit(otp)) setOtp(""); } finally { setLoading(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Enter the OTP sent to the remitter's mobile.</p>
        <input className="np-input text-center font-mono text-lg tracking-[0.4em]" inputMode="numeric" maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid={`${testid}-input`} />
        <Btn loading={loading} disabled={otp.length < 4} onClick={go} data-testid={`${testid}-submit`}>Verify OTP</Btn>
      </DialogContent>
    </Dialog>
  );
};

export function RegisterRemitter({ mobile, onDone }) {
  const [aadhaar, setAadhaar] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);
  const { busy, call } = useCall();
  const send = async (e) => { e.preventDefault(); if (await call("reg", () => api.post("/dmt/remitter/register", { mobile, aadhaar }), "OTP sent")) setOtpOpen(true); };
  const verify = async (otp) => { const ok = await call("v", () => api.post("/dmt/remitter/verify", { mobile, otp }), "Remitter verified"); if (ok) { setOtpOpen(false); onDone(); } return ok; };
  return (
    <Card title={`Register remitter ${mobile}`}>
      <form onSubmit={send} className="flex max-w-lg items-end gap-3" data-testid="remitter-register-form">
        <Field label="Remitter Aadhaar Number" className="flex-1"><input className="np-input font-mono" inputMode="numeric" maxLength={12} value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))} required data-testid="rem-aadhaar-input" /></Field>
        <Btn type="submit" loading={busy === "reg"} disabled={aadhaar.length !== 12} data-testid="rem-register-button">Send OTP</Btn>
      </form>
      <p className="mt-2 text-xs text-slate-400">Aadhaar is sent encrypted to the bank partner and is not stored on this portal.</p>
      <OtpDialog open={otpOpen} title="Verify remitter" onSubmit={verify} onClose={() => setOtpOpen(false)} testid="rem-otp" />
    </Card>
  );
}

export function RemitterKyc({ mobile, wadh, message, onDone }) {
  const { busy, call } = useCall();
  const scan = async () => {
    try {
      const [bio, geo] = await Promise.all([captureFinger(wadh), getGeo()]);
      if (await call("kyc", () => api.post("/dmt/remitter/kyc", { mobile, biometric: bio, ...geo }), "eKYC successful")) onDone();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card title="Remitter eKYC">
      <p className="text-sm text-slate-600" data-testid="rem-kyc-message">{message || "Remitter fingerprint eKYC is required before sending money."}</p>
      <Btn className="mt-4" loading={busy === "kyc"} onClick={scan} data-testid="rem-kyc-scan"><Fingerprint className="h-4 w-4" />Scan remitter fingerprint</Btn>
      <p className="mt-2 text-xs text-slate-400">Needs a registered fingerprint device (Mantra / Morpho / Startek) with RD service on this computer.</p>
    </Card>
  );
}

export function AddBene({ open, onClose, mobile, onDone }) {
  const blank = { name: "", bank_id: "", account_no: "", ifsc: "", mobile: "" };
  const [f, setF] = useState(blank);
  const [banks, setBanks] = useState([]);
  const [pending, setPending] = useState(null);
  const { busy, call } = useCall();
  useEffect(() => { if (open && !banks.length) api.get("/dmt/banks").then(({ data }) => setBanks(data)).catch((e) => toast.error(errMsg(e))); }, [open, banks.length]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickBank = (e) => { const b = banks.find((x) => x.id === e.target.value); setF({ ...f, bank_id: e.target.value, ifsc: b?.ifsc && !f.ifsc ? b.ifsc : f.ifsc }); };
  const submit = async (e) => {
    e.preventDefault();
    const d = await call("add", () => api.post("/dmt/beneficiary", { ...f, remitter_mobile: mobile }));
    if (!d) return;
    if (d.otp_required) setPending(d.bene_id); else { toast.success("Beneficiary added"); setF(blank); onDone(); onClose(); }
  };
  const verify = async (otp) => {
    const ok = await call("v", () => api.post("/dmt/beneficiary/verify", { remitter_mobile: mobile, bene_id: pending, otp }), "Beneficiary added");
    if (ok) { setPending(null); setF(blank); onDone(); onClose(); }
    return ok;
  };
  return (
    <>
      <Dialog open={open && !pending} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="add-bene-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Beneficiary</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Bank"><Select options={banks.map((b) => ({ value: b.id, label: b.name }))} placeholder={banks.length ? "Select Bank" : "Loading banks..."} value={f.bank_id} onChange={pickBank} required data-testid="bene-bank-select" /></Field>
            <Field label="IFSC"><input className="np-input font-mono uppercase" maxLength={11} value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} required data-testid="bene-ifsc-input" /></Field>
            <Field label="Account Number"><input className="np-input font-mono" inputMode="numeric" value={f.account_no} onChange={(e) => setF({ ...f, account_no: e.target.value.replace(/\D/g, "") })} required data-testid="bene-account-input" /></Field>
            <Field label="Beneficiary Name"><input className="np-input" value={f.name} onChange={set("name")} required data-testid="bene-name-input" /></Field>
            <Field label="Beneficiary Mobile (optional)"><input className="np-input" maxLength={10} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "") })} data-testid="bene-mobile-input" /></Field>
            <Btn type="submit" loading={busy === "add"} className="w-full" data-testid="bene-save-button">Save Beneficiary</Btn>
          </form>
        </DialogContent>
      </Dialog>
      <OtpDialog open={!!pending} title="Verify beneficiary" onSubmit={verify} onClose={() => setPending(null)} testid="bene-otp" />
    </>
  );
}

export function TransferDialog({ bene, mobile, remitter, onClose, onDone }) {
  const [f, setF] = useState({ amount: "", mode: "IMPS", otp: "", mpin: "" });
  const [otpFor, setOtpFor] = useState(null);
  const [geo, setGeo] = useState(null);
  const { busy, call } = useCall();
  const { loading, receipt, setReceipt, run } = useSubmit(onDone);
  useEffect(() => { getGeo().then(setGeo).catch((e) => toast.error(e.message)); }, []);
  const amt = Number(f.amount || 0);
  const charge = Math.max(10, amt * 0.01);
  const sendOtp = async () => { if (await call("otp", () => api.post("/dmt/send-otp", { remitter_mobile: mobile, amount: amt }), "OTP sent")) setOtpFor(amt); };
  const modes = [remitter.imps && "IMPS", remitter.neft && "NEFT"].filter(Boolean);
  return (
    <>
      <Dialog open={!!bene && !receipt} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-display">Send money to {bene?.name}</DialogTitle></DialogHeader>
          <div className="rounded-md bg-slate-50 p-3 text-xs">{bene?.bank} · <span className="font-mono">{bene?.account_no}</span> · {bene?.ifsc}</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={`Amount (100 - ${Math.min(5000, remitter.limit.per_txn)})`}><input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => { setF({ ...f, amount: e.target.value }); setOtpFor(null); }} data-testid="transfer-amount-input" /></Field>
            <Field label="Mode"><Select options={modes} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="transfer-mode-select" /></Field>
            <Field label="OTP">
              <div className="flex gap-2">
                <input className="np-input font-mono" inputMode="numeric" maxLength={8} value={f.otp} onChange={(e) => setF({ ...f, otp: e.target.value.replace(/\D/g, "") })} disabled={otpFor !== amt} data-testid="transfer-otp-input" />
                <Btn type="button" variant="outline" loading={busy === "otp"} disabled={amt < 100} onClick={sendOtp} data-testid="send-otp-button">{otpFor === amt ? "Resend" : "Send OTP"}</Btn>
              </div>
            </Field>
            <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
          </div>
          <div className="text-xs text-slate-500" data-testid="transfer-charge">Charge: <b>{inr(charge)}</b> · Total debit: <b>{inr(amt + charge)}</b>{!geo && <span className="ml-2 text-amber-600">Waiting for location…</span>}</div>
          <Btn loading={loading} disabled={!geo || otpFor !== amt || f.otp.length < 4 || f.mpin.length !== 4}
            onClick={() => run("/dmt/transfer", { ...f, ...geo, amount: amt, remitter_mobile: mobile, bene_id: bene.id }, "Transfer submitted")} data-testid="confirm-transfer-button">Confirm Transfer</Btn>
        </DialogContent>
      </Dialog>
      <ReceiptDialog txn={receipt} onClose={() => { setReceipt(null); onClose(); }} />
    </>
  );
}

export function RecentTransfers({ mobile, reload }) {
  const [rows, setRows] = useState([]);
  const [refundFor, setRefundFor] = useState(null);
  const { busy, call } = useCall();
  const load = () => api.get("/dmt/recent", { params: { mobile } }).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, [mobile, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const check = async (t) => { if (await call(t.txn_id, () => api.post(`/dmt/status/${t.txn_id}`))) { toast.success("Status updated"); load(); } };
  const startRefund = async (t) => { if (await call(`r${t.txn_id}`, () => api.post(`/dmt/refund-otp/${t.txn_id}`), "OTP sent")) setRefundFor(t.txn_id); };
  const refund = async (otp) => { const ok = await call("rf", () => api.post(`/dmt/refund/${refundFor}`, { otp }), "Refunded to wallet"); if (ok) { setRefundFor(null); load(); } return ok; };
  if (!rows.length) return null;
  return (
    <Card title="Recent transfers">
      <div className="divide-y text-sm" data-testid="dmt-recent">
        {rows.map((t) => (
          <div key={t.txn_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`dmt-txn-${t.txn_id}`}>
            <div><div className="font-semibold">{inr(t.amount)} → {t.details.bene_name}</div><div className="font-mono text-xs text-slate-500">{t.txn_id} · {t.details.utr ? `UTR ${t.details.utr}` : t.created_at.slice(0, 16).replace("T", " ")}</div></div>
            <div className="flex items-center gap-2">
              <StatusBadge status={t.status} />
              {t.status === "PENDING" && <Btn variant="outline" className="h-8 text-xs" loading={busy === t.txn_id} onClick={() => check(t)} data-testid={`dmt-status-${t.txn_id}`}>Check status</Btn>}
              {t.status === "PENDING" && t.details.refund_allowed && <Btn variant="outline" className="h-8 text-xs" loading={busy === `r${t.txn_id}`} onClick={() => startRefund(t)} data-testid={`dmt-refund-${t.txn_id}`}>Refund</Btn>}
            </div>
          </div>
        ))}
      </div>
      <OtpDialog open={!!refundFor} title="Refund to wallet" onSubmit={refund} onClose={() => setRefundFor(null)} testid="refund-otp" />
    </Card>
  );
}
